const ACTIVE_VH = 0.76;
const INACTIVE_VH = 0.54;
const ACTIVE_MAX_VW = 0.78;
const INACTIVE_MAX_VW = 0.56;
const GAP_VW = 0.04;
const CARD_RATIO = 2 / 3;
const TRANSITION_MS = 900;

class MinerwaCategories extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-stage]');
    this.track = this.querySelector('[data-track]');
    this.clipLayer = this.querySelector('[data-clip-layer]');
    this.explore = this.querySelector('[data-explore]');
    this.cards = [...this.querySelectorAll('[data-card]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.titles = [...this.querySelectorAll('[data-title]')];
    if (this.cards.length === 0) return;

    this.index = 0;
    this.timer = null;
    this.rafId = null;
    this.autoplayMs = (Number(this.dataset.autoplay) || 0) * 1000;
    this.pageWrapper = this.closest('.page-wrapper') || document.querySelector('.page-wrapper');

    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.cards.forEach((card, i) => {
      card.addEventListener('click', (event) => {
        if (i === this.index) return;
        event.preventDefault();
        this.setIndex(i);
        this.restartAutoplay();
      });
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.setIndex(i);
        this.restartAutoplay();
      });
    });

    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.pageWrapper?.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.startAutoplay();
          this.trackClip();
        } else {
          this.stopAutoplay();
          this.stopTracking();
        }
      },
      { threshold: 0 },
    );
    this.observer.observe(this);

    this.measure();
    this.setIndex(0, true);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    this.pageWrapper?.removeEventListener('scroll', this.onScroll);
    this.stopAutoplay();
    this.stopTracking();
    this.observer?.disconnect();
  }

  measure() {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    this.activeHeight = Math.min(ACTIVE_VH * vh, ACTIVE_MAX_VW * vw / CARD_RATIO);
    this.inactiveHeight = Math.min(INACTIVE_VH * vh, INACTIVE_MAX_VW * vw / CARD_RATIO);
    this.activeWidth = this.activeHeight * CARD_RATIO;
    this.inactiveWidth = this.inactiveHeight * CARD_RATIO;
    this.gap = GAP_VW * vw;

    this.style.setProperty('--minerwa-cat-active-h', `${this.activeHeight}px`);
    this.style.setProperty('--minerwa-cat-inactive-h', `${this.inactiveHeight}px`);
    this.style.setProperty('--minerwa-cat-gap', `${this.gap}px`);
  }

  offsetFor(index) {
    const count = this.cards.length;
    const totalWidth =
      this.activeWidth + (count - 1) * this.inactiveWidth + (count - 1) * this.gap;
    const activeLeft = index * (this.inactiveWidth + this.gap);
    const activeCenter = activeLeft + this.activeWidth / 2;
    return totalWidth / 2 - activeCenter;
  }

  setIndex(index, immediate = false) {
    this.index = index;

    this.cards.forEach((card, i) => {
      const distance = Math.abs(i - index);
      card.toggleAttribute('data-active', i === index);
      card.toggleAttribute('data-neighbour', distance === 1);
    });

    this.dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    this.titles.forEach((title, i) => {
      title.toggleAttribute('data-current', i % this.cards.length === index);
    });

    if (immediate) {
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${this.offsetFor(index)}px)`;
      void this.track.offsetWidth;
      this.track.style.transition = '';
    } else {
      this.track.style.transform = `translateX(${this.offsetFor(index)}px)`;
    }

    if (this.explore) this.explore.href = this.cards[index].getAttribute('href');

    this.trackClip();
  }

  updateClip() {
    const card = this.cards[this.index];
    if (!card || !this.clipLayer) return;

    const layerRect = this.clipLayer.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    const top = cardRect.top - layerRect.top;
    const left = cardRect.left - layerRect.left;
    const right = layerRect.right - cardRect.right;
    const bottom = layerRect.bottom - cardRect.bottom;

    this.clipLayer.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`;
  }

  trackClip() {
    this.stopTracking();
    const started = performance.now();
    const loop = () => {
      this.updateClip();
      if (performance.now() - started < TRANSITION_MS) {
        this.rafId = requestAnimationFrame(loop);
      } else {
        this.rafId = null;
      }
    };
    loop();
  }

  stopTracking() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  onScroll() {
    this.updateClip();
  }

  onResize() {
    this.measure();
    this.setIndex(this.index, true);
  }

  startAutoplay() {
    if (this.timer || !this.autoplayMs) return;
    this.timer = setInterval(() => {
      this.setIndex((this.index + 1) % this.cards.length);
    }, this.autoplayMs);
  }

  stopAutoplay() {
    clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('minerwa-categories')) {
  customElements.define('minerwa-categories', MinerwaCategories);
}
